import type { ReactElement } from "react";
import type { GetServerSideProps, GetServerSidePropsContext } from "next";
import { useHydrateAtoms } from "jotai/utils";
import type { LayoutProps } from "@/components/layouts/default-layout/default-layout.layout";
import Layout from "@/components/layouts/default-layout/default-layout.layout";
import HeadWithMetaTags from "@/components/site-areas/head-with-meta-tags";
import WorkPage from "@/components/site-areas/work/work-page/work-page";
import { buildWorkStructuredData } from "@/components/site-areas/work/work-structured-data";
import { CookieKeysEnum } from "@/infrastructure/cookies/cookie.service";
import { CookieForwardingPolicy } from "@/infrastructure/cookies/cookie-forwarding-policy.service";
import { createSsrClient } from "@/infrastructure/http/ssr-client.service";
import { logError } from "@/infrastructure/observability/error-logging.service";
import { getSiteStripeBannerVariants } from "@/services/header.service";
import type { SiteStripeBannerVariants } from "@/services/header.service";
import { fetchUserInfoSsr } from "@/services/userinfo-api.service";
import { getWorkPageData } from "@/services/work.service";
import { userInfoBase } from "@/store/account.store";
import { getDefaultUserInfo, type IUserInfo } from "@/types/account.types";
import type { WorkPageData } from "@/types/work.types";
import { AppCookieValue } from "@/types/shared.types";
import { isUsingApp, requestHasAppHeader } from "@/utility/request.utility";
import { vNextUrl } from "@/utility/url";

interface ServerSideProps {
  appCookie: string | null;
  data: WorkPageData;
  selectionQueryResolved: boolean | null;
  initialSelectionIsRecommended: boolean;
  userInfo: IUserInfo;
  siteStripeBannerVariants: SiteStripeBannerVariants | null;
}

const PRIVATE_WORK_CACHE_CONTROL =
  "private, no-store, must-revalidate, max-age=0";

const ROUTE_QUERY_PARAMETERS = new Set(["slug", "idWork"]);
const SELECTION_QUERY_PARAMETERS = [
  "isbn",
  "edition",
  "idAmazon",
  "idiq",
] as const;

/**
 * Appends the request query parameters to the given destination URL,
 * excluding the route-specific parameters defined in ROUTE_QUERY_PARAMETERS.
 * Replaces any existing query parameters with the same name in the destination URL.
 * This is useful for preserving additional query parameters when navigating between pages while
 * excluding route-specific parameters.
 * @param destination - The base URL to which query parameters will be appended.
 * @param query - The query object from the server-side context.
 * @returns The destination URL with the appended query parameters.
 */
const withRequestQuery = (
  destination: string,
  query: GetServerSidePropsContext["query"],
): string => {
  const url = new URL(destination, "https://placeholder.invalid"); // using the URL class to manipulate query parameters safely

  for (const [name, rawValue] of Object.entries(query)) {
    if (ROUTE_QUERY_PARAMETERS.has(name) || rawValue === undefined) continue;

    url.searchParams.delete(name);

    for (const value of Array.isArray(rawValue) ? rawValue : [rawValue]) {
      url.searchParams.append(name, value);
    }
  }

  return `${url.pathname}${url.search}${url.hash}`;
};

const getFirstQueryValue = (
  value: string | string[] | undefined,
): string | undefined => (Array.isArray(value) ? value[0] : value);

const getPositiveQueryId = (
  value: string | string[] | undefined,
): number | undefined => {
  const parsed = Number(getFirstQueryValue(value));
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const requestHasAccountOrCart = (
  cookies: Partial<Record<string, string>>,
): boolean =>
  Boolean(
    cookies[CookieKeysEnum.AccountAuth] ||
    cookies[CookieKeysEnum.CartIdentifier],
  );

const configureWorkResponseCaching = (ctx: GetServerSidePropsContext): void => {
  // The SSR document contains current price, inventory, and the selection-score
  // recommendation. Keep it out of browser and shared CDN caches until the stable
  // catalog document is separated from that volatile commerce projection.
  ctx.res.setHeader("Cache-Control", PRIVATE_WORK_CACHE_CONTROL);
};

const getWorkPageServerSideProps: GetServerSideProps<ServerSideProps> = async (
  ctx,
) => {
  const idWork = Number(ctx.params?.idWork);
  const requestedSlug = String(ctx.params?.slug ?? "");
  const requestedIsbn = getFirstQueryValue(ctx.query.isbn)?.trim() || undefined;
  const requestedIdIq = getPositiveQueryId(ctx.query.idiq);
  const requestedIdAmazon = getPositiveQueryId(
    ctx.query.edition ?? ctx.query.idAmazon,
  );
  const hasSelectionQuery = SELECTION_QUERY_PARAMETERS.some((name) =>
    Object.prototype.hasOwnProperty.call(ctx.query, name),
  );

  if (!Number.isSafeInteger(idWork) || idWork <= 0) return { notFound: true };

  configureWorkResponseCaching(ctx);

  const hasAccountOrCart = requestHasAccountOrCart(ctx.req.cookies);
  const workClient = createSsrClient(
    ctx,
    hasAccountOrCart
      ? CookieForwardingPolicy.Work
      : CookieForwardingPolicy.PublicWork,
  );
  const isAppRequest = Boolean(
    ctx.req.cookies[CookieKeysEnum.ThriftBooksApp] ||
    requestHasAppHeader(ctx.req.headers),
  );
  const shouldFetchUserInfo = hasAccountOrCart || isAppRequest;
  const userInfoRequest = shouldFetchUserInfo
    ? fetchUserInfoSsr(createSsrClient(ctx, CookieForwardingPolicy.UserInfo))
    : Promise.resolve(null);

  const [workResult, userInfoResult, siteStripeBannerVariantsResult] =
    await Promise.allSettled([
      getWorkPageData(idWork, workClient, {
        isbn: requestedIsbn,
        idIq: requestedIdIq,
        idAmazon: requestedIdAmazon,
      }),
      userInfoRequest,
      getSiteStripeBannerVariants(isAppRequest, false, workClient),
    ]);

  if (workResult.status === "rejected") {
    logError(workResult.reason, { context: "work:getWorkPageData" });
    throw workResult.reason;
  }

  const data = workResult.value;
  if (data.redirectUrl) {
    return {
      redirect: {
        destination: withRequestQuery(data.redirectUrl, ctx.query),
        permanent: true,
      },
    };
  }

  if (!data.work) return { notFound: true };

  if (requestedSlug !== data.work.cleanUrl) {
    return {
      redirect: {
        destination: withRequestQuery(
          `/w/${data.work.cleanUrl}/${data.work.idWork}/`,
          ctx.query,
        ),
        permanent: true,
      },
    };
  }

  let userInfo = getDefaultUserInfo();
  if (userInfoResult.status === "rejected") {
    logError(userInfoResult.reason, { context: "work:fetchUserInfoSsr" });
  } else if (userInfoResult.value?.data) {
    userInfo = userInfoResult.value.data;
  }

  let siteStripeBannerVariants: SiteStripeBannerVariants | null = null;
  if (siteStripeBannerVariantsResult.status === "rejected") {
    logError(siteStripeBannerVariantsResult.reason, {
      context: "work:getSiteStripeBannerVariants",
    });
  } else {
    siteStripeBannerVariants = siteStripeBannerVariantsResult.value;
  }

  return {
    props: {
      appCookie: isAppRequest ? AppCookieValue : null,
      data,
      selectionQueryResolved: hasSelectionQuery
        ? data.selectionResolved === true
        : null,
      initialSelectionIsRecommended:
        !hasSelectionQuery || data.selectionResolved !== true,
      siteStripeBannerVariants,
      userInfo,
    },
  };
};

export const getServerSideProps = getWorkPageServerSideProps;

const WorkPageRoute = ({
  appCookie,
  data,
  selectionQueryResolved,
  initialSelectionIsRecommended,
  userInfo,
}: ServerSideProps) => {
  useHydrateAtoms([[userInfoBase, userInfo]]);

  const work = data.work;
  if (!work) return null;

  const author = data.authors[0]?.name;
  const canonicalUrl = vNextUrl(
    `/w/${work.cleanUrl}/${work.idWork}/`,
  ).toString();
  const title = `${work.title}${author ? ` by ${author}` : ""}`;
  const description = work.synopsis
    ? `Buy a cheap copy of ${title}. ${work.synopsis.slice(0, 150)}${work.synopsis.length > 150 ? "..." : ""}`
    : `Buy new and used editions of ${title} from ThriftBooks.`;
  const structuredData = buildWorkStructuredData(data);

  return (
    <>
      <HeadWithMetaTags
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        imageUrl={work.imageUrlLarge ?? work.imageUrl}
        structuredData={structuredData ?? undefined}
      />
      <WorkPage
        data={data}
        selectionQueryResolved={selectionQueryResolved}
        isLoggedIn={userInfo.isLoggedIn}
        shouldDisplayBreadcrumbs={!isUsingApp(appCookie)}
        initialSelectionIsRecommended={initialSelectionIsRecommended}
      />
    </>
  );
};

WorkPageRoute.getLayout = function getLayout(
  page: ReactElement,
  layoutProps: LayoutProps,
) {
  return (
    <Layout
      appCookie={layoutProps.appCookie}
      userInfo={layoutProps.userInfo}
      siteStripeBannerVariants={layoutProps.siteStripeBannerVariants}
      showDivider
      className="noSmallScreenPadding"
    >
      {page}
    </Layout>
  );
};

export default WorkPageRoute;
